import { call, put, takeEvery, takeLatest } from 'redux-saga/effects'
import {
  AdminCreateRemoteConfig,
  AdminDeleteRemoteConfig,
  AdminFetchRemoteConfigs,
  AdminUpdateRemoteConfig,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import type {
  CreateRemoteConfigPayload,
  DeleteRemoteConfigPayload,
  RemoteConfig,
  RemoteConfigListResponse,
  UpdateRemoteConfigPayload,
} from 'src/interfaces/remoteConfig'
import type { AsyncAction } from 'src/utils/reduxUtils'
import { resolveRemoteConfigErrorCode } from 'src/utils/remoteConfig/remoteConfigErrors'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { captureSentryError } from 'src/utils/sentry'
import { callSecureApi } from './api/callApi'

export function* onFetchRemoteConfigs(action: AsyncAction<void>) {
  try {
    const { payload }: { payload: RemoteConfigListResponse } = yield call(
      callSecureApi,
      Endpoints.AdminRemoteConfigs,
      { method: 'GET' },
    )

    const remoteConfigs = payload?.items ?? []

    yield put(AdminFetchRemoteConfigs.success(remoteConfigs))

    if (action.next) {
      action.next(null, remoteConfigs)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.remoteConfig.onFetchRemoteConfigs] error' })

    yield put(AdminFetchRemoteConfigs.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load remote config keys'))
    }
  }
}

// The write sagas hand `next` the API's error name, so the modal can show it under the right field.
export function* onCreateRemoteConfig(action: AsyncAction<CreateRemoteConfigPayload>) {
  try {
    const { key, app, type, value, enabled, description } = action.payload
    const { payload }: { payload: RemoteConfig } = yield call(
      callSecureApi,
      Endpoints.AdminRemoteConfigs,
      {
        method: 'POST',
        body: { key, app, type, value, enabled, description },
      },
    )

    yield put(AdminCreateRemoteConfig.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.remoteConfig.onCreateRemoteConfig] error' })

    yield put(AdminCreateRemoteConfig.failed(error))

    if (action.next) {
      action.next(resolveRemoteConfigErrorCode(error))
    }
  }
}

export function* onUpdateRemoteConfig(action: AsyncAction<UpdateRemoteConfigPayload>) {
  try {
    const { remoteConfigId, value, enabled, description } = action.payload
    const { payload }: { payload: RemoteConfig } = yield call(
      callSecureApi,
      Endpoints.AdminRemoteConfig,
      {
        method: 'PATCH',
        params: [{ field: ':remoteConfigId', value: remoteConfigId }],
        body: { value, enabled, description },
      },
    )

    yield put(AdminUpdateRemoteConfig.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.remoteConfig.onUpdateRemoteConfig] error' })

    yield put(AdminUpdateRemoteConfig.failed(error))

    if (action.next) {
      action.next(resolveRemoteConfigErrorCode(error))
    }
  }
}

export function* onDeleteRemoteConfig(action: AsyncAction<DeleteRemoteConfigPayload>) {
  try {
    const { remoteConfigId } = action.payload

    yield call(callSecureApi, Endpoints.AdminRemoteConfig, {
      method: 'DELETE',
      params: [{ field: ':remoteConfigId', value: remoteConfigId }],
    })

    yield put(AdminDeleteRemoteConfig.success(remoteConfigId))

    if (action.next) {
      action.next(null, remoteConfigId)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.remoteConfig.onDeleteRemoteConfig] error' })

    yield put(AdminDeleteRemoteConfig.failed(error))

    if (action.next) {
      action.next(resolveRemoteConfigErrorCode(error))
    }
  }
}

export function* remoteConfigSaga() {
  yield takeLatest(AdminFetchRemoteConfigs.type.REQUEST, onFetchRemoteConfigs)
  yield takeEvery(AdminCreateRemoteConfig.type.REQUEST, onCreateRemoteConfig)
  yield takeEvery(AdminUpdateRemoteConfig.type.REQUEST, onUpdateRemoteConfig)
  yield takeEvery(AdminDeleteRemoteConfig.type.REQUEST, onDeleteRemoteConfig)
}
